import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Edit, Trash2, Settings } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import ConfirmPopup from './ui/ConfirmPopup';

interface Brand {
  id: string;
  name: string;
  description: string;
  website?: string;
  contactEmail?: string;
  createdAt: string;
}

interface BrandManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const BrandManagementModal: React.FC<BrandManagementModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState<string | null>(null);
  const [editingBrand, setEditingBrand] = useState<Brand | null>(null);

  // Formulario para nueva marca
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    website: '',
    contactEmail: ''
  });

  useEffect(() => {
    if (isOpen && user) {
      loadBrands();
    }
  }, [isOpen, user]);

  // Manejar tecla ESC para cerrar modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleCloseModal();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  const handleCloseModal = () => {
    // Resetear estado al cerrar
    setShowAddForm(false);
    setEditingBrand(null);
    setFormData({
      name: '',
      description: '',
      website: '',
      contactEmail: ''
    });
    onClose();
  };

  const loadBrands = async () => {
    setLoading(true);
    try {
      // Cargar marcas desde el servicio de inventario
      const inventoryService = (await import('../services/inventoryService')).default;
      const brandsData = await inventoryService.getBrandsFromDocumentedStructure();
      setBrands(brandsData);
    } catch (error) {
      console.error('Error cargando marcas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBrand = async () => {
    if (!user || !formData.name) return;
    
    try {
      if (editingBrand) {
        // Editar marca existente
        const updatedBrand: Brand = {
          ...editingBrand,
          name: formData.name,
          description: formData.description,
          website: formData.website,
          contactEmail: formData.contactEmail
        };

        setBrands(prev => prev.map(b => b.id === editingBrand.id ? updatedBrand : b));
        setEditingBrand(null);
        console.log('✅ Marca editada:', updatedBrand.name);
      } else {
        // Agregar nueva marca usando optimizedInventoryService
        const optimizedService = (await import('../services/optimizedInventoryService')).default;
        const brandId = await optimizedService.createBrand({
          name: formData.name,
          description: formData.description,
          website: formData.website
        });

        const newBrand: Brand = {
          id: brandId,
          name: formData.name,
          description: formData.description,
          website: formData.website,
          contactEmail: formData.contactEmail,
          createdAt: new Date().toISOString().split('T')[0]
        };

        setBrands(prev => [...prev, newBrand]);
        console.log('✅ Marca creada en Firebase:', newBrand.name, 'ID:', brandId);
      }

      setFormData({ name: '', description: '', website: '', contactEmail: '' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error agregando/editando marca:', error);
    }
  };

  const handleEditBrand = (brand: Brand) => {
    setEditingBrand(brand);
    setFormData({
      name: brand.name,
      description: brand.description,
      website: brand.website || '',
      contactEmail: brand.contactEmail || ''
    });
    setShowAddForm(true);
  };

  const handleCancelEdit = () => {
    setEditingBrand(null);
    setFormData({ name: '', description: '', website: '', contactEmail: '' });
    setShowAddForm(false);
  };

  const handleDeleteBrand = (brandId: string) => {
    setBrandToDelete(brandId);
    setShowConfirmPopup(true);
  };

  const handleConfirmDelete = async () => {
    if (!brandToDelete) return;
    
    try {
      // Eliminar de Firebase usando inventoryService
      const inventoryService = (await import('../services/inventoryService')).default;
      await inventoryService.deleteBrand(brandToDelete);
      
      // Eliminar del estado local
      setBrands(prev => prev.filter(b => b.id !== brandToDelete));
      console.log(`✅ Marca eliminada de Firebase: ${brandToDelete}`);
    } catch (error) {
      console.error('Error eliminando marca:', error);
    } finally {
      setBrandToDelete(null);
      setShowConfirmPopup(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
            className="fixed inset-0 bg-black/50 z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`fixed inset-4 z-50 ${
              isDark ? 'bg-slate-900' : 'bg-white'
            } rounded-2xl shadow-2xl overflow-hidden`}
          >
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${
                isDark ? 'border-slate-700' : 'border-gray-200'
              }`}>
                <div className="flex items-center space-x-3">
                  <Settings className={`${isDark ? 'text-purple-400' : 'text-purple-600'}`} size={24} />
                  <h2 className={`text-2xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('brandManagement')}
                  </h2>
                </div>
                <button
                  onClick={handleCloseModal}
                  className={`p-2 rounded-xl transition-colors duration-200 ${
                    isDark 
                      ? 'hover:bg-slate-800 text-gray-300 hover:text-white' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-hidden">
                {!showAddForm ? (
                  // Vista principal
                  <div className="h-full flex flex-col">
                    {/* Header con controles */}
                    <div className={`p-6 border-b ${
                      isDark ? 'border-slate-700' : 'border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className={`text-lg font-semibold ${
                            isDark ? 'text-white' : 'text-gray-900'
                          }`}>
                            {t('myBrands')}
                          </h3>
                          <p className={`text-sm ${
                            isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {t('manageProductBrands')}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            console.log('🔄 Abriendo formulario de Nueva Marca');
                            setShowAddForm(true);
                          }}
                          className="btn-primary flex items-center space-x-2 px-6 py-2"
                        >
                          <Plus size={20} />
                          <span>{t('newBrand')}</span>
                        </button>
                      </div>
                    </div>

                    {/* Lista de marcas */}
                    <div className="flex-1 overflow-y-auto p-6">
                      {loading ? (
                        <div className="flex items-center justify-center h-64">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
                        </div>
                      ) : brands.length === 0 ? (
                        <div className="text-center py-12">
                          <Settings className={`mx-auto mb-4 ${
                            isDark ? 'text-gray-600' : 'text-gray-400'
                          }`} size={48} />
                          <p className={`text-lg ${
                            isDark ? 'text-gray-400' : 'text-gray-600'
                          }`}>
                            {t('noBrandsRegistered')}
                          </p>
                          <p className={`text-sm mt-2 ${
                            isDark ? 'text-gray-500' : 'text-gray-500'
                          }`}>
                            {t('addBrandsToOrganize')}
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {brands.map((brand) => (
                            <div
                              key={brand.id}
                              className={`p-6 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-700 border-slate-600' 
                                  : 'bg-white border-gray-200'
                              } hover:shadow-lg transition-shadow duration-200`}
                            >
                              <div className="flex justify-between items-start mb-4">
                                <div>
                                  <h3 className={`text-lg font-semibold ${
                                    isDark ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    {brand.name}
                                  </h3>
                                  <p className={`text-sm mt-1 ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    {brand.description}
                                  </p>
                                </div>
                              </div>
                              <div className="space-y-2 mb-4">
                                {brand.website && (
                                  <p className={`text-sm ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    <span className="font-medium">{t('website')}</span> {brand.website}
                                  </p>
                                )}
                                {brand.contactEmail && (
                                  <p className={`text-sm ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    <span className="font-medium">{t('email')}</span> {brand.contactEmail}
                                  </p>
                                )}
                              </div>
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleEditBrand(brand)}
                                  className={`p-2 rounded-lg transition-colors duration-200 ${
                                    isDark 
                                      ? 'hover:bg-slate-700 text-gray-400 hover:text-white' 
                                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                                  }`}
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteBrand(brand.id)}
                                  className={`p-2 rounded-lg transition-colors duration-200 ${
                                    isDark 
                                      ? 'hover:bg-slate-700 text-gray-400 hover:text-red-400' 
                                      : 'hover:bg-gray-100 text-gray-600 hover:text-red-600'
                                  }`}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Formulario de agregar/editar marca
                  <div className="h-full flex flex-col">
                    {/* Header del formulario */}
                    <div className={`p-6 border-b ${
                      isDark ? 'border-slate-700' : 'border-gray-200'
                    }`}>
                      <div className="flex justify-between items-center">
                        <h3 className={`text-xl font-bold ${
                          isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                          {editingBrand ? t('editBrand') : t('newBrand')}
                        </h3>
                        <button
                          onClick={handleCancelEdit}
                          className={`p-2 rounded-xl transition-colors duration-200 ${
                            isDark 
                              ? 'hover:bg-slate-800 text-gray-300 hover:text-white' 
                              : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>

                    {/* Contenido del formulario */}
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="space-y-6">
                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Nombre de la Marca *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                            placeholder={t('brandNamePlaceholder')}
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            Descripción
                          </label>
                          <textarea
                            value={formData.description}
                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                            placeholder={t('brandDescriptionPlaceholder')}
                            rows={3}
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            {t('website')}
                          </label>
                          <input
                            type="url"
                            value={formData.website}
                            onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                            placeholder="https://ejemplo.com"
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>

                        <div>
                          <label className={`block text-sm font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-700'
                          }`}>
                            {t('email')}
                          </label>
                          <input
                            type="email"
                            value={formData.contactEmail}
                            onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e.target.value }))}
                            placeholder="contacto@marca.com"
                            className={`w-full px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Footer del formulario */}
                    <div className={`p-6 border-t ${
                      isDark ? 'border-slate-700' : 'border-gray-200'
                    }`}>
                      <div className="flex justify-end space-x-4">
                        <button
                          onClick={handleCancelEdit}
                          className="btn-secondary px-6 py-2"
                        >
                          {t('cancel')}
                        </button>
                        <button
                          onClick={handleAddBrand}
                          className="btn-primary px-6 py-2"
                        >
                          {editingBrand ? t('saveBrand') : t('addBrand')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Popup */}
              <ConfirmPopup
                isOpen={showConfirmPopup}
                onClose={() => {
                  setShowConfirmPopup(false);
                  setBrandToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                title={t('deleteBrand')}
                message="¿Estás seguro de que quieres eliminar esta marca?\n\nEsta acción no se puede deshacer."
                type="danger"
                confirmText={t('delete')}
                cancelText={t('cancel')}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BrandManagementModal;